/**
 * Dispatch（调度中心 / 派车单）API client
 * 契约源：apps/api-server/src/api_server/routes/dispatch.py（prefix /api/v1/dispatch）
 */
import { api } from "./api";
import type { OrderRead } from "./types/orders";

export interface DispatchLegRead {
  id: string;
  order_id: string;
  leg_order: number;
  leg_type: string;
  pax_count: number;
  pickup_at: string;
  pickup_location: string;
  dropoff_at: string | null;
  dropoff_location: string | null;
  /** DISPFIX-1 Part 6 · pending/in_progress/done/skipped——skipped=该订单已从这趟车撤下，不计入占用/拼车人数 */
  status: string;
}

export interface DispatchOrderRead {
  id: string;
  dispatch_number: string;
  driver_user_id: string | null;
  vehicle_id: string | null;
  status: string; // scheduled / in_progress / completed / cancelled
  pickup_at: string;
  pickup_location: string;
  dropoff_at: string | null;
  dropoff_location: string | null;
  total_pax: number;
  time_zone: string;
  internal_notes: string | null;
  driver_instructions: string | null;
  legs: DispatchLegRead[];
  created_at: string;
  updated_at: string;
}

export interface DispatchOrderCreatePayload {
  driver_user_id?: string | null;
  vehicle_id?: string | null;
  pickup_at: string;
  pickup_location: string;
  dropoff_at?: string | null;
  dropoff_location?: string | null;
  total_pax?: number;
  internal_notes?: string | null;
  driver_instructions?: string | null;
  legs?: Array<{
    order_id: string;
    leg_order: number;
    leg_type: string;
    pax_count: number;
    pickup_at: string;
    pickup_location: string;
    dropoff_at?: string | null;
    dropoff_location?: string | null;
  }>;
}

function buildQuery(params: Record<string, unknown>): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null) usp.append(k, String(v));
  }
  const s = usp.toString();
  return s ? `?${s}` : "";
}

/** GET /api/v1/dispatch/orders */
export function listDispatchOrders(q?: {
  status?: string; driver_user_id?: string; pickup_from?: string; pickup_to?: string; limit?: number; offset?: number;
}): Promise<DispatchOrderRead[]> {
  return api(`/api/v1/dispatch/orders${buildQuery((q ?? {}) as Record<string, unknown>)}`);
}

/** GET /api/v1/dispatch/orders/{id} */
export function getDispatchOrder(id: string): Promise<DispatchOrderRead> {
  return api(`/api/v1/dispatch/orders/${id}`);
}

/** GET /api/v1/dispatch/by-order/{salesOrderId} — 按销售订单反查派车单（无则 null）*/
export function getDispatchByOrder(salesOrderId: string): Promise<DispatchOrderRead | null> {
  return api(`/api/v1/dispatch/by-order/${salesOrderId}`);
}

/** POST /api/v1/dispatch/orders */
export function createDispatchOrder(payload: DispatchOrderCreatePayload): Promise<DispatchOrderRead> {
  return api(`/api/v1/dispatch/orders`, { method: "POST", body: JSON.stringify(payload) });
}

/** PATCH /api/v1/dispatch/orders/{id} */
export function updateDispatchOrder(id: string, payload: Partial<DispatchOrderCreatePayload> & { status?: string }): Promise<DispatchOrderRead> {
  return api(`/api/v1/dispatch/orders/${id}`, { method: "PATCH", body: JSON.stringify(payload) });
}

/** DISPVOID-1 · PATCH /api/v1/dispatch/orders/{id}/cancel — 作废派车单（清理孤儿工具，
 * 不是「取消派单」的替代品）。理由必填；409 = 状态冲突/该单是订单当前生效派车单。 */
export function cancelDispatchOrder(id: string, payload: { reason: string }): Promise<DispatchOrderRead> {
  return api(`/api/v1/dispatch/orders/${id}/cancel`, { method: "PATCH", body: JSON.stringify(payload) });
}

/** POST /api/v1/dispatch/orders/{dispatchId}/legs — 关联销售订单到派车单 */
export function appendDispatchLeg(dispatchId: string, payload: {
  order_id: string; leg_order: number; leg_type: string; pax_count: number;
  pickup_at: string; pickup_location: string; dropoff_location?: string | null;
}): Promise<DispatchLegRead> {
  return api(`/api/v1/dispatch/orders/${dispatchId}/legs`, { method: "POST", body: JSON.stringify(payload) });
}

/**
 * BATCH-2 · POST /api/v1/dispatch:batch-assign —— 唯一权威派单端点（口径 8·用户裁决
 * 「路线 A」：批量端点是唯一实现，单单派单 = N=1 的特例）。取代 DISPFIX-1 的单单原子端点
 * `POST /orders/{id}:dispatch`（该端点保留在后端不删，只是派单向导不再调它）。
 * 契约源：docs/设计稿-批量派单与司导待定-2026-09-04.md §4.1（BATCH-1 后端并行实现，
 * 形状已写死，不用等对方交付）。
 */
export interface BatchAssignPayload {
  order_ids: string[];
  vehicle_id: string;
  driver_id?: string | null;        // 口径 7 · 可空 = 司导待定
  pickup_at: string;
  pickup_location: string;
  internal_notes?: string | null;
  override_reason?: string | null;  // 容量覆盖 · op_supervisor+
  override_reason_code?: string | null;   // REASON-2 · 超载派车原因代码（capacity_override）
  dispatch_order_id?: string | null; // 传了 = 追加到该车次（屏 03）
  allow_additional_leg?: boolean;
}
export interface BatchAssignResult {
  orders: OrderRead[];
  dispatch: DispatchOrderRead;
}
export function batchAssignDispatch(payload: BatchAssignPayload): Promise<BatchAssignResult> {
  return api(`/api/v1/dispatch:batch-assign`, { method: "POST", body: JSON.stringify(payload) });
}

/**
 * DISPUX-1 · GET /api/v1/dispatch/states?order_ids= —— 批量查订单的派车状态（一次 SQL）：已挂哪张派车单 + 还缺哪些派车要素 + 队列按钮。
 * 缺项（vehicle / driver / pickup）与按钮文案的定义只在后端 services/dispatch_assign（dispatch_missing / dispatch_action_label），前端只展示，不另写一套。
 */
export interface DispatchState {
  order_id: string;
  has_dispatch: boolean;
  dispatch_id: string | null;
  dispatch_number: string | null;
  run_state: string | null;
  pickup_at: string | null;
  pickup_location: string | null;
  vehicle_id: string | null;
  vehicle_label: string | null;
  driver_id: string | null;
  driver_label: string | null;
  total_pax: number;
  missing: { code: "vehicle" | "driver" | "pickup" | string; label: string }[];
  /** dispatch = 新建派单；reassign = 补车 / 改派（订单已有存活派车单） */
  action: "dispatch" | "reassign";
  /** 派单 / 补车 / 补司导 / 补车 / 司导 / 补集合信息 / 确认派单 */
  action_label: string;
}
export function getDispatchStates(orderIds: string[]): Promise<{ items: DispatchState[] }> {
  return api(`/api/v1/dispatch/states?order_ids=${encodeURIComponent(orderIds.join(","))}`);
}

/** DISPUX-1 · POST /api/v1/dispatch:reassign —— 补车 / 改派：改原派车单的车辆 / 司导（不新建、不新增 leg；订单 待派 → 已派） */
export interface ReassignPayload {
  order_ids: string[];
  vehicle_id?: string | null;
  driver_id?: string | null;
  internal_notes?: string | null;
  override_reason?: string | null;
  override_reason_code?: string | null;
}
export function reassignDispatch(payload: ReassignPayload): Promise<{ orders: OrderRead[]; dispatches: DispatchOrderRead[] }> {
  return api(`/api/v1/dispatch:reassign`, { method: "POST", body: JSON.stringify(payload) });
}

/**
 * BATCH-2 屏 04 · GET /api/v1/dispatch/pending-driver —— 待补司导队列（草拟契约·
 * 设计稿 §3.4 没给 JSON 形状，字段按工单口径 7 + 预审给的清单拟定，
 * 待 BATCH-1 Part 5 落地时对齐/如需调整）。
 * 口径：车已定·司导未定 = assigned_driver_id IS NULL 的 assigned 态订单（不新增订单
 * 状态，见设计稿 §3.3）。距发团在前端用 pickup_at 现算，不依赖后端算好的小时数——
 * 少一处两边口径要对齐的地方。
 */
export interface PendingDriverItem {
  dispatch_id: string;
  dispatch_number: string;
  pickup_at: string;
  product_name: string | null;
  vehicle_id: string;
  vehicle_label: string;
  seat_capacity: number;
  total_pax: number;
  /** 该车次挂的销售订单——补司导即对这些订单逐一 PATCH /orders/{id}/assign（只传 driver_id） */
  order_ids: string[];
}
export function listPendingDriverDispatches(): Promise<PendingDriverItem[]> {
  return api(`/api/v1/dispatch/pending-driver`);
}

/** P2T2 · 拼车合并建议（只读·人工确认）*/
export interface CarpoolSuggestion {
  order_ids: string[];
  order_nos: string[];
  product_id: string | null;
  product_name: string | null;
  travel_date: string | null;
  total_pax: number;
  suggested_vehicle_id: string | null;
  suggested_vehicle_label: string | null;
  vehicle_capacity: number | null;
  occupancy_pct: number | null;
  empty_seats: number | null;
  languages: string[];
  rationale: string;
}

/** GET /api/v1/dispatch/carpool-suggestions?date= */
export function getCarpoolSuggestions(date: string): Promise<CarpoolSuggestion[]> {
  return api(`/api/v1/dispatch/carpool-suggestions?date=${encodeURIComponent(date)}`);
}

/** 智能匹配建议 POST /api/v1/dispatch/auto-suggest */
export function autoSuggest(payload: {
  orders: Array<{ id: string; pickup_at: string; pickup_location: string; dropoff_location?: string; pax_count: number }>;
  constrain_vehicle_ids?: string[]; constrain_driver_ids?: string[];
}): Promise<{ suggestions: any[]; feasible_count: number; infeasible_count: number }> {
  return api(`/api/v1/dispatch/auto-suggest`, { method: "POST", body: JSON.stringify(payload) });
}

export interface DriverMatch {
  driver_id: string;
  driver_user_id: string | null;
  full_name: string;
  match_score: number;   // 0-1
  match_pct: number;     // 0-100
  language_ok: boolean;
  trainee: boolean;
  dims: Record<string, number>;
}

/** 给某订单的所有 active 司导逐个算 8 维匹配分（派单向导逐卡展示）*/
export function getDriverMatch(orderId: string): Promise<DriverMatch[]> {
  return api(`/api/v1/dispatch/order/${orderId}/driver-match`);
}
